import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError, todayStr } from "../lib/api";

const VISUAL = {
  libero: "bg-white border-zinc-300 text-zinc-700",
  riservato: "bg-amber-100 border-amber-400 text-amber-900",
  occupato: "bg-emerald-500 border-emerald-600 text-white",
};

export default function Sala() {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showRoom, setShowRoom] = useState(false);
  const [showTable, setShowTable] = useState(null);
  const [roomName, setRoomName] = useState("");
  const [tableForm, setTableForm] = useState({ number: "", seats: 4 });

  const load = useCallback(() => {
    api.get(`/sala/overview?date=${todayStr()}`).then((r) => setRooms(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const addRoom = async (e) => {
    e.preventDefault();
    try {
      await api.post("/rooms", { name: roomName });
      toast.success("Sala creata");
      setRoomName(""); setShowRoom(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const addTable = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/rooms/${showTable.id}/tables`, {
        number: tableForm.number, seats: parseInt(tableForm.seats) || 4,
        x: showTable.tables.length % 4, y: Math.floor(showTable.tables.length / 4),
      });
      toast.success("Tavolo aggiunto");
      setTableForm({ number: "", seats: 4 }); setShowTable(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-5" data-testid="sala-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Panoramica sala</h1>
        <button onClick={() => setShowRoom(true)} data-testid="btn-nuova-sala"
          className="h-10 px-4 rounded-md bg-white border border-zinc-300 text-sm font-semibold text-zinc-700 flex items-center gap-2">
          <Plus size={15} weight="bold" /> Sala
        </button>
      </div>

      <div className="flex gap-3 text-xs text-zinc-500">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-white border border-zinc-300 inline-block" /> Libero</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-100 border border-amber-400 inline-block" /> Riservato</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block" /> Occupato</span>
      </div>

      {rooms.map((room) => (
        <section key={room.id} className="bg-white border border-zinc-200 rounded-lg p-4" data-testid={`room-${room.id}`}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-semibold text-zinc-900">{room.name}</h2>
            <button onClick={() => setShowTable(room)} data-testid={`btn-add-table-${room.id}`}
              className="h-9 px-3 rounded-md bg-zinc-100 text-xs font-semibold text-zinc-700 flex items-center gap-1.5 hover:bg-zinc-200">
              <Plus size={13} weight="bold" /> Tavolo
            </button>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2.5">
            {room.tables.map((t) => (
              <button key={t.id} onClick={() => setSelected(t)} data-testid={`table-${t.id}`}
                className={`aspect-square rounded-lg border-2 flex flex-col items-center justify-center gap-0.5 transition active:scale-95 ${VISUAL[t.visual_status]}`}>
                <span className="font-display font-bold text-lg">T{t.number}</span>
                <span className="text-[10px] font-medium opacity-80">{t.seats} coperti</span>
                {t.reservation && (
                  <span className="text-[10px] font-semibold truncate max-w-full px-1">
                    {t.reservation.time} · {t.reservation.customer_name.split(" ")[0]}
                  </span>
                )}
              </button>
            ))}
          </div>
        </section>
      ))}

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setSelected(null)}>
          <div className="bg-white w-full sm:max-w-sm rounded-t-xl sm:rounded-lg p-5" onClick={(e) => e.stopPropagation()} data-testid="table-dialog">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-lg">Tavolo {selected.number}</h3>
              <button onClick={() => setSelected(null)} className="p-2 text-zinc-500"><X size={18} /></button>
            </div>
            <p className="text-sm text-zinc-500 mb-4">{selected.seats} coperti · Stato: <strong className="capitalize">{selected.visual_status}</strong></p>
            {selected.reservation ? (
              <div className="space-y-3">
                <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 text-sm">
                  <div className="font-semibold text-zinc-900">{selected.reservation.customer_name}</div>
                  <div className="text-zinc-500">{selected.reservation.time} · {selected.reservation.party_size} persone</div>
                </div>
                <button onClick={() => navigate(`/prenotazioni/${selected.reservation.id}`)} data-testid="table-open-reservation"
                  className="w-full h-11 rounded-md bg-zinc-900 text-white text-sm font-semibold">
                  Apri prenotazione
                </button>
              </div>
            ) : (
              <button onClick={() => navigate("/prenotazioni")} data-testid="table-new-reservation"
                className="w-full h-11 rounded-md bg-zinc-900 text-white text-sm font-semibold">
                Nuova prenotazione per questo tavolo
              </button>
            )}
          </div>
        </div>
      )}

      {showRoom && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowRoom(false)}>
          <form onSubmit={addRoom} className="bg-white w-full max-w-xs rounded-lg p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="room-dialog">
            <h3 className="font-display font-bold">Nuova sala</h3>
            <input required value={roomName} onChange={(e) => setRoomName(e.target.value)} placeholder="Nome sala" data-testid="room-name-input"
              className="w-full h-11 px-3 rounded-md border border-zinc-300 text-sm" />
            <button className="w-full h-11 rounded-md bg-zinc-900 text-white text-sm font-semibold" data-testid="room-submit">Crea</button>
          </form>
        </div>
      )}

      {showTable && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowTable(null)}>
          <form onSubmit={addTable} className="bg-white w-full max-w-xs rounded-lg p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="table-form-dialog">
            <h3 className="font-display font-bold">Nuovo tavolo — {showTable.name}</h3>
            <input required value={tableForm.number} onChange={(e) => setTableForm({ ...tableForm, number: e.target.value })}
              placeholder="Numero tavolo" data-testid="table-number-input"
              className="w-full h-11 px-3 rounded-md border border-zinc-300 text-sm" />
            <input type="number" min="1" value={tableForm.seats} onChange={(e) => setTableForm({ ...tableForm, seats: e.target.value })}
              placeholder="Coperti" data-testid="table-seats-input"
              className="w-full h-11 px-3 rounded-md border border-zinc-300 text-sm" />
            <button className="w-full h-11 rounded-md bg-zinc-900 text-white text-sm font-semibold" data-testid="table-submit">Aggiungi</button>
          </form>
        </div>
      )}
    </div>
  );
}
